import React from 'react';
import './TermsOfServicePage.css';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="tos-page">
      <div className="tos-content">
        <h1>CompanySieve Terms of Service</h1>
        <p><strong>Effective Date:</strong> March 5, 2026</p>
        <p><strong>Last Updated:</strong> March 5, 2026</p>

        <hr />

        <h2>1. Introduction</h2>
        <p>
          These Terms of Service ("Terms") govern your access to and use of CompanySieve ("Service"),
          operated by CompanySieve, LLC ("Company," "we," "us," or "our"). The Service is accessible at
          companysieve.com.
        </p>
        <p>
          By creating an account or using the Service, you agree to be bound by these Terms. If you do not
          agree, do not use the Service.
        </p>

        <hr />

        <h2>2. Description of Service</h2>
        <p>
          CompanySieve is a software tool that assists users in conducting export compliance screening. The
          Service:
        </p>
        <ul>
          <li>Searches the U.S. Consolidated Screening List (CSL) maintained by the International Trade Administration</li>
          <li>Optionally screens against international sanctions databases via OpenSanctions (covering EU, UK, UN, and other jurisdictions)</li>
          <li>Uses artificial intelligence to enrich company profiles, validate name matches, analyze corporate structures, and generate research summaries</li>
          <li>Provides domain registration intelligence via WHOIS data</li>
          <li>Generates downloadable PDF compliance memoranda</li>
        </ul>

        <hr />

        <h2>3. Important Disclaimers</h2>

        <h3>3.1 Not Legal Advice</h3>
        <p>
          <strong>The Service does not provide legal advice.</strong> Screening results, risk assessments,
          AI-generated research, executive briefs, and all other outputs are informational tools to assist your
          compliance process. They are not a substitute for the judgment of qualified legal counsel or a
          licensed export compliance professional.
        </p>

        <h3>3.2 Not a Comprehensive Compliance Program</h3>
        <p>
          Use of CompanySieve does not constitute a complete export compliance program. You remain solely
          responsible for:
        </p>
        <ul>
          <li>Conducting end-use and end-user analysis as required under the Export Administration Regulations (EAR) and other applicable laws</li>
          <li>Obtaining any required export licenses</li>
          <li>Verifying screening results independently before relying on them for transactional decisions</li>
          <li>Maintaining compliance with all applicable export control, sanctions, and anti-boycott laws</li>
        </ul>

        <h3>3.3 AI-Generated Content</h3>
        <p>
          Portions of the Service's output are generated by artificial intelligence, including company research,
          match validation reasoning, corporate structure analysis, and executive briefs. AI-generated content
          may contain inaccuracies, omissions, or outdated information. You must independently verify any
          AI-generated output before relying on it.
        </p>

        <h3>3.4 Data Currency</h3>
        <p>
          Screening list data is sourced from third-party government and public databases that are updated on
          their own schedules. Results reflect the data available at the time of screening and may not include
          the most recent additions or removals. <strong>You should re-screen before each transaction.</strong>
        </p>

        <hr />

        <h2>4. User Accounts</h2>

        <h3>4.1 Registration</h3>
        <p>
          You must create an account to use the Service. You agree to provide accurate information during
          registration and to keep your account credentials secure. You are responsible for all activity under
          your account.
        </p>

        <h3>4.2 Account Security</h3>
        <p>
          You must not share your login credentials with others. If you become aware of unauthorized access to
          your account, notify us immediately at support@companysieve.com.
        </p>

        <h3>4.3 Account Termination</h3>
        <p>
          We may suspend or terminate your account if you violate these Terms or use the Service in a manner
          that is harmful, fraudulent, or abusive. You may request account deletion at any time by contacting
          support@companysieve.com.
        </p>

        <hr />

        <h2>5. Permitted Use</h2>

        <h3>5.1 Authorized Uses</h3>
        <p>
          The Service is intended for lawful export compliance screening by businesses, compliance
          professionals, trade practitioners, and legal advisors.
        </p>

        <h3>5.2 Prohibited Uses</h3>
        <p>You may not:</p>
        <ul>
          <li>Use the Service for any unlawful purpose</li>
          <li>Attempt to circumvent usage limits, rate limits, or access controls</li>
          <li>Reverse-engineer, scrape, or systematically extract data from the Service</li>
          <li>Resell, sublicense, or redistribute the Service or its outputs without our written consent</li>
          <li>Submit false, misleading, or fraudulent screening queries</li>
          <li>Use the Service to facilitate sanctions evasion or export control violations</li>
          <li>Share account credentials or allow unauthorized third parties to access your account</li>
        </ul>

        <hr />

        <h2>6. Fees and Payment</h2>

        <h3>6.1 Pricing</h3>
        <p>
          The Service is offered under subscription plans as described on our website. We reserve the right to
          change pricing with 30 days' notice.
        </p>

        <h3>6.2 Invoicing</h3>
        <p>
          We will invoice you for Service usage according to your subscription plan. Payment is due within 30
          days of invoice date unless otherwise agreed in writing.
        </p>

        <h3>6.3 Usage Limits</h3>
        <p>
          Your plan includes a defined number of screenings per month. If you exceed your plan's screening
          limit, additional screenings will be unavailable until the next billing period or until you upgrade
          your plan.
        </p>

        <hr />

        <h2>7. Data and Privacy</h2>

        <h3>7.1 Data We Collect</h3>
        <p>We collect:</p>
        <ul>
          <li><strong>Account information:</strong> Email address, name, and hashed password</li>
          <li><strong>Screening inputs:</strong> Company names, domains, and country codes you submit for screening</li>
          <li><strong>Screening results:</strong> The full results of each screening, including matches, risk assessments, and AI research, stored as part of your screening history</li>
          <li><strong>Usage data:</strong> Number of screenings, AI token usage, and timestamps</li>
        </ul>

        <h3>7.2 How We Protect Your Data</h3>
        <ul>
          <li>Company names in screening records are encrypted at rest</li>
          <li>Passwords are hashed using industry-standard algorithms</li>
          <li>Communication with the Service is encrypted via HTTPS</li>
        </ul>

        <h3>7.3 Third-Party Data Sharing</h3>
        <p>
          To provide the Service, we transmit your screening inputs (company names, countries, domains) to the
          following third-party services:
        </p>
        <ul>
          <li><strong>U.S. International Trade Administration</strong> (CSL API)</li>
          <li><strong>OpenSanctions</strong> (international sanctions screening)</li>
          <li><strong>Google</strong> (Gemini AI for research and entity resolution)</li>
          <li><strong>OpenAI</strong> (fallback AI provider)</li>
          <li><strong>IP2WHOIS</strong> (domain registration data)</li>
        </ul>
        <p>
          We do not sell your data to third parties. We share data with third-party services only as necessary
          to operate the Service as described above.
        </p>

        <h3>7.4 Data Retention</h3>
        <p>
          Screening results are retained in your account history until you delete them or request account
          deletion. You may delete individual screening records at any time through the Service.
        </p>

        <h3>7.5 Data Deletion</h3>
        <p>
          You may request complete deletion of your account and all associated data by contacting
          support@companysieve.com. We will process deletion requests within 30 days.
        </p>

        <hr />

        <h2>8. Intellectual Property</h2>

        <h3>8.1 Our IP</h3>
        <p>
          The Service, including its design, code, AI prompts, and methodology, is the property of CompanySieve,
          LLC. These Terms do not grant you any ownership interest in the Service.
        </p>

        <h3>8.2 Your Data</h3>
        <p>
          You retain ownership of the data you submit to the Service and the screening reports generated for
          your account. We claim no ownership over your screening inputs or outputs.
        </p>

        <hr />

        <h2>9. Limitation of Liability</h2>

        <h3>9.1 No Warranty</h3>
        <p className="tos-uppercase">
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS,
          IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT.
        </p>
        <p>We do not warrant that:</p>
        <ul>
          <li>The Service will be uninterrupted, error-free, or secure</li>
          <li>Screening results will be complete, accurate, or current</li>
          <li>AI-generated content will be free of errors or omissions</li>
          <li>The Service will identify all relevant matches on any screening list</li>
        </ul>

        <h3>9.2 Limitation of Damages</h3>
        <p className="tos-uppercase">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANYSIEVE, LLC SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS,
          BUSINESS INTERRUPTION, REGULATORY PENALTIES, FINES, OR LEGAL COSTS ARISING FROM YOUR USE OF OR
          RELIANCE ON THE SERVICE.
        </p>
        <p className="tos-uppercase">
          OUR TOTAL LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN
          THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
        </p>

        <h3>9.3 Allocation of Risk</h3>
        <p>
          You acknowledge that the Service is a screening assistance tool, not a compliance guarantee. The fees
          charged for the Service reflect this allocation of risk.
        </p>

        <hr />

        <h2>10. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless CompanySieve, LLC, its officers, employees, and agents from
          any claims, losses, damages, liabilities, and expenses (including legal fees) arising from:
        </p>
        <ul>
          <li>Your use of the Service</li>
          <li>Your violation of these Terms</li>
          <li>Your reliance on screening results for business decisions</li>
          <li>Any violation of export control or sanctions laws in connection with your use of the Service</li>
        </ul>

        <hr />

        <h2>11. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes by email or
          through the Service at least 30 days before the changes take effect. Your continued use of the
          Service after the effective date of updated Terms constitutes acceptance.
        </p>

        <hr />

        <h2>12. Governing Law and Disputes</h2>
        <p>
          These Terms are governed by the laws of the State of New York, without regard to conflict of law
          principles. Any disputes arising under these Terms shall be resolved in the state or federal courts
          located in Erie County, New York.
        </p>

        <hr />

        <h2>13. Miscellaneous</h2>
        <ul>
          <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and us regarding the Service.</li>
          <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions remain in effect.</li>
          <li><strong>Waiver:</strong> Failure to enforce any provision of these Terms does not constitute a waiver of that provision.</li>
          <li><strong>Assignment:</strong> You may not assign your rights under these Terms without our consent. We may assign our rights at any time.</li>
        </ul>

        <hr />

        <h2>14. Contact</h2>
        <p>For questions about these Terms, contact us at:</p>
        <p><strong>Email:</strong> support@companysieve.com</p>

        <hr />

        <p className="tos-draft-notice">
          <em>This document is a draft and should be reviewed by a licensed attorney before publication.</em>
        </p>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
